import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { initializeGoogleAnalytics } from '@/lib/googleAnalytics';

import { CookieConsentModal } from './CookieConsentModal';

interface CookieConsentProps {
  privacyUrl?: string;
}

export function CookieConsent({
  privacyUrl = '/politica-privacidade',
}: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setConsent, shouldShowBanner } = useCookieConsent();

  useEffect(() => {
    if (shouldShowBanner()) {
      setIsVisible(true);
    }
  }, [shouldShowBanner]);

  if (!isVisible) return null;

  const handleAcceptAll = () => {
    setConsent({
      analytics: true,
      essential: true,
      consentDate: new Date().toISOString(),
      consentVersion: import.meta.env.VITE_COOKIE_CONSENT_VERSION || '1.0',
    });
    // Carregar GA imediatamente após aceitar
    initializeGoogleAnalytics();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    setConsent({
      analytics: false,
      essential: true,
      consentDate: new Date().toISOString(),
      consentVersion: import.meta.env.VITE_COOKIE_CONSENT_VERSION || '1.0',
    });
    setIsVisible(false);
  };

  return (
    <>
      <div
        role="region"
        aria-label="Aviso de cookies e privacidade"
        aria-describedby="cookie-description"
        className="fixed bottom-4 right-4 z-40 max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        <div className="rounded-lg border border-wine/20 bg-white p-4 shadow-lg dark:bg-slate-950 dark:border-wine/10">
          <h2
            id="cookie-title"
            className="text-sm font-semibold text-wine mb-2"
          >
            🍷 Cookies & Privacidade
          </h2>

          <p
            id="cookie-description"
            className="text-xs text-slate-600 dark:text-slate-300 mb-4 leading-relaxed"
          >
            Usamos cookies essenciais para funcionar e Google Analytics para
            entender como você usa nosso site. Nenhum dado pessoal é coletado
            sem sua autorização.
          </p>

          <div className="flex gap-2 flex-col sm:flex-row">
            <Button
              variant="wine"
              size="sm"
              onClick={handleAcceptAll}
              className="flex-1"
              aria-label="Aceitar todos os cookies"
            >
              Aceitar tudo
            </Button>

            <Button
              variant="wine-outline"
              size="sm"
              onClick={handleRejectAll}
              className="flex-1"
              aria-label="Rejeitar cookies de analytics"
            >
              Rejeitar
            </Button>

            <button
              onClick={() => setIsModalOpen(true)}
              className="text-xs text-wine hover:underline self-center font-medium"
              aria-label="Ler política de privacidade completa"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <CookieConsentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
